/**
 * EDM (e-Fatura / e-Arşiv) gönderim durumu — Invoice Records ve Customer Invoices ortak mantığı.
 *
 * Kapsam: yalnızca kesilmiş (Issued) satış faturaları EDM'ye gönderilir; taslak, iptal, alış
 * faturaları ve iade belgeleri bu analizde "kapsam dışı" sayılır (iade faturaları kendi detay
 * sayfasından gönderilir).
 *
 * Durumlar:
 *  - sent         EDM kabul etti ve son GİB sorgusu bir red / hata bildirmiyor
 *  - gibRejected  EDM kabul etti ama son GİB sorgusu red / hata döndü
 *  - failed       EDM'ye gönderim denendi, hata alındı
 *  - notSent      henüz gönderilmedi
 *  - na           kapsam dışı
 */
import React from "react";
import { TextField, MenuItem, Tooltip } from "@mui/material";
import CloudDone from "@mui/icons-material/CloudDone";
import ScheduleSend from "@mui/icons-material/ScheduleSend";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import GppBad from "@mui/icons-material/GppBad";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { financeText } from "finance-language";
import { COLORS, SPACE, cardSx, getTone, FinanceSectionHeader } from "./FinanceUiKit";

const lower = (value) => String(value || "").trim().toLowerCase();

// GİB durumu EDM'den serbest metin olarak gelir; red / hata ifadeleri buna göre ayırt edilir
const GIB_REJECTION_PATTERN = /REDDED|REJECT|HATA|ERROR|FAIL|İPTAL|IPTAL|GEÇERSİZ|GECERSIZ/i;

export const getEdmInfo = (invoice) => invoice?.edm || {};

export const isEdmEligible = (invoice) => {
    const status = lower(invoice?.invoiceStatus || invoice?.customerInvoiceStatus);
    const documentType = lower(invoice?.documentType || "invoice");
    return lower(invoice?.invoiceType) !== "purchase" && documentType === "invoice" && status === "issued";
};

export const isGibRejected = (invoice) => {
    const edm = getEdmInfo(invoice);
    return GIB_REJECTION_PATTERN.test(`${edm.gibStatus || ""} ${edm.gibStatusDescription || ""}`);
};

export const getEdmState = (invoice) => {
    if (!isEdmEligible(invoice)) return "na";
    const status = lower(getEdmInfo(invoice).status);
    if (status === "sent") return isGibRejected(invoice) ? "gibRejected" : "sent";
    if (status === "failed") return "failed";
    return "notSent";
};

export const EDM_STATES = {
    sent: { label: "Sent to EDM", tone: "green", icon: CloudDone, hint: "Accepted by EDM" },
    notSent: { label: "Not sent", tone: "amber", icon: ScheduleSend, hint: "Issued, waiting to be sent" },
    failed: { label: "Send failed", tone: "red", icon: ErrorOutline, hint: "EDM returned an error" },
    gibRejected: { label: "Rejected by GİB", tone: "red", icon: GppBad, hint: "Last GİB check reported a rejection" },
    na: { label: "Not applicable", tone: "navy", icon: null, hint: "Draft, cancelled, purchase or return document" },
};

export const EDM_FILTER_OPTIONS = ["all", "sent", "notSent", "failed", "gibRejected"];
export const EDM_PROFILE_OPTIONS = ["all", "e-Fatura", "e-Arsiv"];

export const matchesEdmFilters = (invoice, state, profile) => {
    const current = getEdmState(invoice);
    if (state !== "all" && current !== state) return false;
    if (profile !== "all") {
        // Profil yalnızca gönderilmiş / denenmiş faturalarda bellidir
        if (lower(getEdmInfo(invoice).profile) !== lower(profile)) return false;
    }
    return true;
};

// EDM profil adı veride ASCII tutulur (e-Arsiv); ekranda Türkçe yazılır
export const displayProfile = (profile) => (lower(profile) === "e-arsiv" ? "e-Arşiv" : profile || "");

const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/** Tablo hücresi: durum etiketi + altında gönderim zamanı / profil; hata nedeni ipucunda. */
export const EdmStatusPill = ({ invoice }) => {
    const state = getEdmState(invoice);
    const meta = EDM_STATES[state];
    const edm = getEdmInfo(invoice);

    if (state === "na") {
        return (
            <MDTypography component="span" sx={{ fontSize: 12, color: COLORS.subtext }}>
                —
            </MDTypography>
        );
    }

    const tone = getTone(meta.tone);
    const Icon = meta.icon;
    const detail =
        state === "failed"
            ? edm.error
            : state === "gibRejected"
                ? `${edm.gibStatus || ""}${edm.gibStatusDescription ? ` — ${edm.gibStatusDescription}` : ""}`
                : edm.gibStatus
                    ? `${financeText("Last checked GİB status")}: ${edm.gibStatus}${edm.gibStatusDescription ? ` — ${edm.gibStatusDescription}` : ""}`
                    : "";
    const subline =
        state === "sent" || state === "gibRejected"
            ? [displayProfile(edm.profile), formatDateTime(edm.sentAt)].filter(Boolean).join(" · ")
            : state === "failed"
                ? financeText("Retry from the actions column")
                : financeText("Waiting to be sent");

    const pill = (
        <MDBox display="inline-flex" flexDirection="column" gap={0.25}>
            <MDBox
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.6,
                    width: "fit-content",
                    px: 1,
                    py: 0.25,
                    borderRadius: SPACE.radiusPill,
                    fontSize: "11.5px",
                    fontWeight: 600,
                    lineHeight: 1.6,
                    whiteSpace: "nowrap",
                    color: tone.color,
                    background: tone.bg,
                }}
            >
                <Icon sx={{ fontSize: "14px !important" }} />
                {financeText(meta.label)}
            </MDBox>
            {subline && (
                <MDTypography component="span" sx={{ fontSize: 11, color: COLORS.subtext, whiteSpace: "nowrap" }}>
                    {subline}
                </MDTypography>
            )}
        </MDBox>
    );

    return detail ? (
        <Tooltip title={detail} arrow>
            <span>{pill}</span>
        </Tooltip>
    ) : (
        pill
    );
};

/**
 * EDM özet kartları. invoices: diğer filtreler uygulanmış (EDM filtresi hariç) liste.
 * formatAmount(list) seçili para biriminde tutar metni döndürür.
 */
export const EdmOverview = ({ invoices, activeState, onSelect, formatAmount }) => {
    const groups = { sent: [], notSent: [], failed: [], gibRejected: [] };
    invoices.forEach((invoice) => {
        const state = getEdmState(invoice);
        if (groups[state]) groups[state].push(invoice);
    });
    const eligible = groups.sent.length + groups.notSent.length + groups.failed.length + groups.gibRejected.length;
    const sentRate = eligible ? Math.round((groups.sent.length / eligible) * 100) : 0;

    return (
        <MDBox sx={{ ...cardSx, p: SPACE.cardPadding }}>
            <FinanceSectionHeader
                title={financeText("e-Invoice (EDM) Status")}
                subtitle={`${eligible} ${financeText("issued sales invoices in the current filters")} · ${financeText("click a card to filter the table")}`}
                action={
                    <MDBox textAlign="right" minWidth={160}>
                        <MDTypography sx={{ fontSize: 12, color: COLORS.subtext }}>{financeText("Sent rate")}</MDTypography>
                        <MDTypography sx={{ fontSize: 20, fontWeight: 700, color: COLORS.text, lineHeight: 1.2 }}>
                            {sentRate}%
                        </MDTypography>
                        <MDBox mt={0.5} sx={{ height: 6, borderRadius: 3, background: "#EEF2F6", overflow: "hidden" }}>
                            <MDBox sx={{ height: "100%", width: `${sentRate}%`, background: COLORS.green, transition: "width .3s ease" }} />
                        </MDBox>
                    </MDBox>
                }
            />
            <MDBox
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }}
                gap={1.5}
            >
                {["sent", "notSent", "failed", "gibRejected"].map((state) => {
                    const meta = EDM_STATES[state];
                    const tone = getTone(meta.tone);
                    const Icon = meta.icon;
                    const active = activeState === state;
                    const list = groups[state];
                    return (
                        <MDBox
                            key={state}
                            component="button"
                            type="button"
                            onClick={() => onSelect(active ? "all" : state)}
                            aria-pressed={active}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                p: 2,
                                textAlign: "left",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                borderRadius: SPACE.radiusTile,
                                background: active ? tone.bg : COLORS.white,
                                border: `1px solid ${active ? tone.color : COLORS.cardBorder}`,
                                boxShadow: active ? `inset 0 0 0 1px ${tone.color}` : "none",
                                transition: "border-color .15s ease, background .15s ease",
                                "&:hover": { borderColor: tone.color },
                            }}
                        >
                            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                <MDBox display="flex" alignItems="center" gap={1}>
                                    <MDBox
                                        width={32}
                                        height={32}
                                        borderRadius={SPACE.radiusTile}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{ color: tone.color, background: tone.bg }}
                                    >
                                        <Icon sx={{ fontSize: "18px !important" }} />
                                    </MDBox>
                                    <MDTypography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                                        {financeText(meta.label)}
                                    </MDTypography>
                                </MDBox>
                                {active && (
                                    <MDTypography sx={{ fontSize: 11, fontWeight: 700, color: tone.color }}>
                                        {financeText("Filtered")}
                                    </MDTypography>
                                )}
                            </MDBox>
                            <MDTypography sx={{ fontSize: 24, fontWeight: 700, color: COLORS.text, lineHeight: 1.1 }}>
                                {list.length}
                            </MDTypography>
                            <MDTypography sx={{ fontSize: 12, color: COLORS.subtext }}>
                                {list.length ? formatAmount(list) : financeText(meta.hint)}
                            </MDTypography>
                        </MDBox>
                    );
                })}
            </MDBox>
        </MDBox>
    );
};

const selectSx = { "& .MuiOutlinedInput-root": { background: "#FFFFFF" } };

const EDM_FILTER_LABELS = {
    all: "All EDM statuses",
    sent: "Sent to EDM",
    notSent: "Not sent",
    failed: "Send failed",
    gibRejected: "Rejected by GİB",
};

/** EDM durumu + profil filtre alanları (mevcut filtre ızgaralarına yerleştirilir). */
export const EdmStatusSelect = ({ value, onChange }) => (
    <TextField select fullWidth size="small" label={financeText("EDM Status")} value={value} onChange={(e) => onChange(e.target.value)} sx={selectSx}>
        {EDM_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
                {financeText(EDM_FILTER_LABELS[option])}
            </MenuItem>
        ))}
    </TextField>
);

export const EdmProfileSelect = ({ value, onChange }) => (
    <TextField select fullWidth size="small" label={financeText("EDM Profile")} value={value} onChange={(e) => onChange(e.target.value)} sx={selectSx}>
        {EDM_PROFILE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
                {option === "all" ? financeText("All profiles") : option === "e-Arsiv" ? "e-Arşiv" : option}
            </MenuItem>
        ))}
    </TextField>
);

/** URL ?edm=notSent gibi bağlantılarla gelen başlangıç filtresi. */
export const readEdmFilterFromUrl = (search) => {
    const value = new URLSearchParams(search || "").get("edm");
    return EDM_FILTER_OPTIONS.includes(value) ? value : "all";
};
